import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import "@/index.css";

import { AuthProvider, useAuth } from "./AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AppHome from "./pages/AppHome";
import Journal from "./pages/Journal";
import Upgrade from "./pages/Upgrade";
import BillingSuccess from "./pages/BillingSuccess";
import AuthCallback from "./pages/AuthCallback";
import Privacy from "./pages/Privacy";

function FontLoader({ children }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&family=Montserrat:wght@200;300;400;500;600&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Use Font Face API if available, else fallback timer
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => setReady(true));
      // Safety fallback in case fonts.ready never resolves
      const t = setTimeout(() => setReady(true), 1200);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setReady(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f5f0e8",
          color: "#1a1008",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="fonts-loading"
      >
        <div style={{ opacity: 0.3, fontSize: 11, letterSpacing: "0.3em" }}>...</div>
      </div>
    );
  }
  return children;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f5f0e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="protected-loading"
      >
        <div style={{ opacity: 0.3, fontSize: 11, letterSpacing: "0.3em", color: "#1a1008" }}>...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function RootRouter() {
  const location = useLocation();
  // Handle OAuth callback synchronously (any path with session_id in hash)
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <Journal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <Upgrade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute>
            <BillingSuccess />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <FontLoader>
      <AuthProvider>
        <BrowserRouter>
          <RootRouter />
        </BrowserRouter>
      </AuthProvider>
    </FontLoader>
  );
}
